import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

interface LedgerEntry {
  id: string;
  type: 'debit' | 'credit';
  amount: string;
  description: string;
  created_at: string;
}

interface LedgerAccount {
  id: string;
  balance: string;
  entries?: LedgerEntry[];
}

export default function LedgerPage() {
  const { data: accountsData, isLoading } = useQuery({
    queryKey: ['ledger-accounts'],
    queryFn: async () => {
      const { data } = await api.get('/ledger/accounts');
      return data;
    },
  });

  const accounts: LedgerAccount[] = accountsData?.accounts ?? [];
  const myAccount = accounts[0];

  // Fetch entries for user's account
  const { data: entriesData } = useQuery({
    queryKey: ['ledger-entries', myAccount?.id],
    queryFn: async () => {
      if (!myAccount?.id) return { entries: [] };
      const { data } = await api.get(`/ledger/accounts/${myAccount.id}/entries`);
      return data;
    },
    enabled: !!myAccount?.id,
  });

  const entries: LedgerEntry[] = entriesData?.entries ?? [];
  const balance = myAccount?.balance ?? '0.00';

  return (
    <>
      <Helmet>
        <title>Ledger — React-City</title>
      </Helmet>

      <div className="space-y-6 animate-in">
        <div>
          <h1 className="text-2xl font-bold text-heading">Ledger</h1>
          <p className="text-muted text-sm mt-1">
            Registro contabile a partita doppia — tutte le tue transazioni
          </p>
        </div>

        {/* Saldo card */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="card bg-gradient-to-br from-primary-50 to-white dark:from-primary-500/10 dark:to-[#242428]">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Saldo disponibile</p>
            <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mt-1">
              {isLoading ? '...' : `€ ${parseFloat(balance).toFixed(2)}`}
            </p>
          </div>
          <div className="card">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Movimenti debit</p>
            <p className="text-2xl font-bold text-heading mt-1">
              {entries.filter(e => e.type === 'debit').length}
            </p>
          </div>
          <div className="card">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Movimenti credit</p>
            <p className="text-2xl font-bold text-accent-500 dark:text-accent-400 mt-1">
              {entries.filter(e => e.type === 'credit').length}
            </p>
          </div>
        </div>

        {/* Transaction history */}
        <div className="card overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4 text-heading">Movimenti</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 dark:border-surface-700">
                <th className="table-header">Data</th>
                <th className="table-header">Descrizione</th>
                <th className="table-header">Tipo</th>
                <th className="text-right pb-3 font-medium text-surface-500 dark:text-surface-400">Importo</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr className="table-row bg-surface-50 dark:bg-surface-800/50">
                  <td className="py-3 text-muted text-center" colSpan={4}>
                    Nessun movimento registrato
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="table-row">
                    <td className="py-3 text-muted">
                      {new Date(entry.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="py-3 font-medium">{entry.description}</td>
                    <td className="py-3">
                      <span className={entry.type === 'debit' ? 'badge-red' : 'badge-green'}>
                        {entry.type}
                      </span>
                    </td>
                    <td className={`py-3 text-right font-mono ${entry.type === 'debit' ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`}>
                      {entry.type === 'debit' ? '-' : '+'} € {parseFloat(entry.amount).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
